import { Link } from "@tanstack/react-router";
import { Copy, Trash2 } from "lucide-react";
import { useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { EnumSelect } from "@/components/shared/enum-select";
import { ErrorState } from "@/components/shared/error-state";
import { CursorPagination } from "@/components/shared/pagination";
import { RowActions } from "@/components/shared/row-actions";
import { SearchInput } from "@/components/shared/search-input";
import { StatusBadge } from "@/components/shared/status-badge";
import {
  type CopyOperationListRow,
  useCopyOperations,
  useDeleteCopyOperation,
} from "@/hooks/use-copy-operations";
import { DEFAULT_LIMIT, useDataGrid } from "@/hooks/use-data-grid";
import { useSession } from "@/lib/auth-client";
import { COPY_OPERATION_STATUS_OPTIONS } from "@/lib/enums";

function isDeletable(row: CopyOperationListRow): boolean {
  return row.status === "pending" || row.status === "failed";
}

function formatDateTime(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

/**
 * The Copy Operations Log — one row per copy session (draft through
 * completed/failed), not per attempt (retries reuse the same row). Standard
 * data-grid wiring; see `src/examples/widget/WidgetsTable.tsx`.
 */
export function CopyOperationsLogTable() {
  const { data: session } = useSession();
  const isAdmin = session?.user?.role === "admin";

  const grid = useDataGrid({ defaultSort: "initiatedAt" });
  const { data, isLoading, isError, error, refetch } = useCopyOperations(
    grid.query,
    grid.pageRequest,
  );
  const [deleteTarget, setDeleteTarget] = useState<CopyOperationListRow | null>(null);
  const deleteOperation = useDeleteCopyOperation();

  const rows = data?.data ?? [];

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    deleteOperation.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
  }

  const toolbar = (
    <div className="flex items-center gap-4">
      <SearchInput
        value={grid.searchInput}
        onValueChange={grid.setSearch}
        placeholder="Search by Contract or Client ID…"
        className="max-w-sm flex-1"
      />
      <EnumSelect
        value={grid.status}
        onValueChange={grid.setStatus}
        options={COPY_OPERATION_STATUS_OPTIONS}
        allOption={{ label: "All statuses" }}
        placeholder="All statuses"
        className="w-[160px]"
        aria-label="Filter by status"
      />
    </div>
  );

  if (isError) {
    return (
      <div className="space-y-4">
        {toolbar}
        <ErrorState
          message={error?.message ?? "Failed to load copy operations"}
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {toolbar}

      <DataTable
        headers={[
          { label: "Status", sortKey: "status" },
          { label: "Source" },
          { label: "Target" },
          { label: "Result Contract ID" },
          { label: "Movements" },
          { label: "Initiated By" },
          { label: "Initiated At", sortKey: "initiatedAt" },
          { label: "Failure Reason" },
          { label: "", className: "w-12" },
        ]}
        sort={grid.sort}
        sortDir={grid.sortDir}
        onSort={grid.toggleSort}
        loading={isLoading}
        empty={
          <EmptyState
            icon={Copy}
            title="No copy operations yet"
            description="Copies initiated from Contract Search will show up here."
          />
        }
      >
        {rows.map((row) => (
          <tr key={row.id} className="border-b border-border last:border-0 hover:bg-muted/30">
            <td className="px-4 py-3">
              <StatusBadge status={row.status} />
            </td>
            <td className="px-4 py-3">
              <Link
                to="/dashboard/copy-operations/$id"
                params={{ id: row.id }}
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                Contract #{row.sourceObjectId}
              </Link>
              <p className="text-xs text-muted-foreground">{row.sourceBuName ?? row.sourceBuId}</p>
            </td>
            <td className="px-4 py-3">
              <p>{row.targetBuName ?? row.targetBuId}</p>
              <p className="text-xs text-muted-foreground">Client {row.targetCustomerId}</p>
            </td>
            <td className="px-4 py-3">{row.resultObjectId ?? "—"}</td>
            <td className="px-4 py-3">{row.movementCount > 0 ? row.movementCount : "—"}</td>
            <td className="px-4 py-3">{row.initiatedByName ?? row.initiatedByEmail ?? "—"}</td>
            <td className="px-4 py-3 text-muted-foreground">{formatDateTime(row.initiatedAt)}</td>
            <td className="px-4 py-3">
              {row.status === "failed" && row.failureReason ? (
                <p
                  className="max-w-[220px] truncate text-xs text-destructive"
                  title={row.failureReason}
                >
                  {row.failureReason}
                </p>
              ) : (
                "—"
              )}
            </td>
            <td className="px-4 py-3 text-right">
              {isAdmin && isDeletable(row) && (
                <RowActions
                  actions={[
                    {
                      label: "Delete",
                      icon: Trash2,
                      destructive: true,
                      onSelect: () => setDeleteTarget(row),
                    },
                  ]}
                />
              )}
            </td>
          </tr>
        ))}
      </DataTable>

      <CursorPagination
        total={data?.total ?? 0}
        page={grid.page}
        limit={DEFAULT_LIMIT}
        hasNext={data?.hasNext ?? false}
        hasPrev={data?.hasPrev ?? false}
        onNext={() => grid.goNext(data?.nextCursor ?? null)}
        onPrev={() => grid.goPrev(data?.prevCursor ?? null)}
        loading={isLoading}
        itemLabel="copy operations"
      />

      {isAdmin && (
        <ConfirmDialog
          open={deleteTarget !== null}
          onOpenChange={(open) => {
            if (!open) setDeleteTarget(null);
          }}
          title="Delete copy operation"
          description={`Delete the copy of Contract #${deleteTarget?.sourceObjectId} to Client ${deleteTarget?.targetCustomerId}? This cannot be undone.`}
          confirmLabel="Delete"
          variant="destructive"
          loading={deleteOperation.isPending}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
}
